<script setup lang="ts">
// Space settings, a menu of sections (one screen when there's only General): General (the
// space's QR code and leaving, for everyone; rename
// the space, by permission; hand over ownership and delete the space, owner), Roles (create, edit and
// order roles) and Members (give each member a role), for whoever manages roles, and Reports
// for moderators. Where it is lives in the URL (?space-settings=…, lib/sectionNav.ts). Roles are ordered, highest first, and the order is the hierarchy: people
// with Manage roles reach only the roles and members below their own. Space and channel
// names stay end-to-end encrypted: the engine encrypts them with the space key. Role names
// are plaintext (the server enforces their permissions).
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { Perm, SpaceInfo, SpaceMember, SpaceRole, SpaceRoleRequest } from '../api/types'
import {
  can,
  grantable,
  isLocked,
  MODERATION,
  PERMS,
  rankOf,
  ROLE_COLORS,
  roleOf,
  roleReach,
  togglePerm as withPerm,
} from '../lib/roles'
import ReportsPanel from './ReportsPanel.vue'
import Avatar from './Avatar.vue'
import PixelEditorModal from './PixelEditorModal.vue'
import SpaceQrModal from './SpaceQrModal.vue'
import { markColor } from '../lib/pattern'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'
import SectionMenu from './SectionMenu.vue'
import { useSectionNav } from '../lib/sectionNav'

const props = defineProps<{ space: SpaceInfo }>()
const session = useSession()
const router = useRouter()

const isOwner = computed(() => props.space.my_role === 'owner')
/** Moderators (any moderation permission) also get the space's reports. */
const moderator = computed(() => MODERATION.some((p) => can(props.space, p)))
type Section = 'general' | 'roles' | 'members' | 'reports'
const nav = useSectionNav<Section>('space-settings')
const sections = computed(() => [
  { id: 'general' as const, label: 'General', hint: 'Name, symbol, QR code, leave' },
  ...(props.space.can_manage_roles
    ? [
        { id: 'roles' as const, label: 'Roles', hint: `${props.space.roles.length} roles, highest first` },
        { id: 'members' as const, label: 'Members', hint: 'Give people roles' },
      ]
    : []),
  ...(moderator.value ? [{ id: 'reports' as const, label: 'Reports', hint: 'Reported messages' }] : []),
])
/** The open section; with only General there's no menu. Losing a role (or moderation)
 *  while open takes its sections away, back to the menu. */
const shown = computed(() =>
  sections.value.length === 1 ? sections.value[0]! : (sections.value.find((s) => s.id === nav.at.value) ?? null),
)
/** The section last open, so the menu puts the focus back on its row. */
const left = ref<string | null>(null)
watch(shown, (now, was) => {
  if (!now && was) left.value = was.id
})
/** The symbol designer and the QR code replace this dialog while open. */
const designing = ref(false)
const showingQr = ref(false)
const canRename = computed(() => can(props.space, 'rename_space'))
const others = computed(() => props.space.members.filter((m) => m.profile.user_id !== session.me))

// --- Custom roles ----------------------------------------------------------------------
/** Roles and members ranked below this are mine to manage (null: none). */
const reach = computed(() => roleReach(props.space, session.me))
const mine = (rank: number) => reach.value !== null && rank > reach.value
const grantablePerms = computed(() => grantable(props.space, session.me))
/** A permission can be added only if I may give it out; one already there may stay. */
const permDisabled = (perms: readonly Perm[], p: Perm, editable = true) =>
  !editable || isLocked(perms, p) || (!perms.includes(p) && !grantablePerms.value.includes(p))
const holders = (r: SpaceRole) => props.space.members.filter((m) => m.roles?.includes(r.id)).length

const drafts = reactive<Record<string, SpaceRoleRequest>>({})
const toDraft = (r: SpaceRole): SpaceRoleRequest => ({ name: r.name, color: r.color, perms: [...r.perms] })
watch(
  () => props.space.roles ?? [],
  (roles) => {
    for (const r of roles) if (!drafts[r.id]) drafts[r.id] = toDraft(r)
  },
  { immediate: true },
)
const fresh = reactive<SpaceRoleRequest>({ name: '', color: ROLE_COLORS[0]!, perms: [] })
const dirty = (r: SpaceRole) => JSON.stringify(drafts[r.id]) !== JSON.stringify(toDraft(r))

function togglePerm(d: SpaceRoleRequest, p: Perm): void {
  d.perms = withPerm(d.perms, p)
}

const createRole = () =>
  run(async () => {
    await session.api.createRole(props.space.id, { ...fresh, name: fresh.name.trim() })
    fresh.name = ''
    fresh.perms = []
    await session.refresh()
  }, 'Role created')

const saveRole = (r: SpaceRole) =>
  run(async () => {
    const saved = await session.api.updateRole(props.space.id, r.id, drafts[r.id]!)
    drafts[r.id] = toDraft(saved)
    await session.refresh()
  }, 'Role saved')

function deleteRole(r: SpaceRole): Promise<void> {
  if (!confirm(`Delete the ${r.name} role? Members lose it, and any libraries shared with it.`)) return Promise.resolve()
  return run(async () => {
    await session.api.deleteRole(props.space.id, r.id)
    delete drafts[r.id]
    await session.refresh()
  }, 'Role deleted')
}

/** Moves a role one place up (-1) or down (+1). */
const moveRole = (i: number, by: -1 | 1) =>
  run(async () => {
    const ids = props.space.roles.map((r) => r.id)
    ;[ids[i], ids[i + by]] = [ids[i + by]!, ids[i]!]
    await session.api.reorderRoles(props.space.id, ids)
    await session.refresh()
  })

// --- Users -----------------------------------------------------------------------------
const userQuery = ref('')
const users = computed(() => {
  const q = userQuery.value.trim().toLowerCase()
  return [...props.space.members]
    .filter((m) => !q || m.profile.user_id.toLowerCase().includes(q) || m.profile.display_name.toLowerCase().includes(q))
    .sort((a, b) => rankOf(props.space, a) - rankOf(props.space, b) || a.profile.display_name.localeCompare(b.profile.display_name))
})

/** Gives a member a role (replacing theirs), or takes theirs away (`''`). */
function setMemberRole(m: SpaceMember, role: string): Promise<void> {
  const held = roleOf(props.space, m)
  return run(async () => {
    if (role) await session.api.assignRole(props.space.id, role, m.profile.user_id)
    else if (held) await session.api.unassignRole(props.space.id, held.id, m.profile.user_id)
    await session.refresh()
  }, 'Role updated')
}
const spaceName = ref(props.space.name)
const busy = ref(false)

async function run(fn: () => Promise<void>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const renameSpace = () => run(() => session.renameSpace(props.space.id, spaceName.value.trim()), 'Space renamed')

const newOwner = ref('')
function transferOwnership(): Promise<void> {
  const m = others.value.find((x) => x.profile.user_id === newOwner.value)
  const top = props.space.roles[0]
  const keep = top ? ` You'll keep the top role, ${top.name}.` : ''
  if (!m || !confirm(`Make ${m.profile.display_name} the owner of ${props.space.name}?${keep}`)) return Promise.resolve()
  return run(async () => {
    await session.setRole(props.space.id, m.profile.user_id, 'owner')
    newOwner.value = ''
  }, 'Ownership handed over')
}

function leave(): Promise<void> {
  if (!confirm(`Leave ${props.space.name}? You'll lose access to its history on this device.`)) return Promise.resolve()
  return run(async () => {
    await session.leaveSpace(props.space.id)
    await router.replace({ name: 'home' })
  })
}
function deleteSpace(): Promise<void> {
  if (!confirm(`Delete ${props.space.name} with all its channels and history, for everyone? This can't be undone.`)) {
    return Promise.resolve()
  }
  return run(async () => {
    await session.deleteSpace(props.space.id)
    await router.replace({ name: 'home' })
  }, 'Space deleted')
}
</script>

<template>
  <PixelEditorModal
    v-if="designing"
    :title="`Symbol for ${space.name}`"
    :initial="space.icon ?? null"
    :seed-id="space.id"
    :seed-color="markColor(space.id, session.palette)"
    :action="(p) => session.setSpaceIcon(space.id, p).then(() => session.toast(p ? 'Symbol saved' : 'Symbol removed'))"
    @close="designing = false"
  />
  <SpaceQrModal v-else-if="showingQr" :space="space" @close="showingQr = false" />
  <Modal
    v-else
    page
    :title="shown && sections.length > 1 ? `${space.name} · ${shown.label}` : 'Space settings'"
    :back="shown && sections.length > 1 ? 'Space settings' : undefined"
    @back="nav.back()"
    @close="nav.close()"
  >
    <SectionMenu v-if="!shown" :sections="sections" :from="left" testid="space-section" @open="(id) => nav.enter(id as Section)" />
    <ReportsPanel v-else-if="shown.id === 'reports'" :space="space" />
    <template v-else-if="shown.id === 'general'">
    <div v-if="canRename" class="mb-4 flex items-center gap-3">
      <Avatar :id="space.id" :name="space.name" :size="40" square :pixels="space.icon" />
      <button class="btn-ghost" data-testid="design-space-icon" @click="designing = true">Design symbol</button>
    </div>
    <form v-if="canRename" class="mb-5 flex gap-2" @submit.prevent="renameSpace">
      <input v-model="spaceName" class="input flex-1" maxlength="64" aria-label="Space name" data-testid="space-name-input" />
      <button class="btn-primary" :disabled="busy || !spaceName.trim()" data-testid="rename-space">Rename</button>
    </form>

    <template v-if="isOwner">
      <h3 class="label mt-6 text-error">Danger zone</h3>
      <form v-if="others.length" class="mb-3 flex gap-2" @submit.prevent="transferOwnership">
        <select v-model="newOwner" class="input min-w-0 flex-1 py-1" aria-label="New owner" data-testid="transfer-owner">
          <option value="" disabled>Hand over ownership to…</option>
          <option v-for="m in others" :key="m.profile.user_id" :value="m.profile.user_id">{{ m.profile.display_name }} · {{ m.profile.user_id }}</option>
        </select>
        <button class="btn-outline" :disabled="busy || !newOwner" data-testid="transfer-owner-submit">Transfer</button>
      </form>
      <button class="btn-danger w-full" :disabled="busy" data-testid="delete-space" @click="deleteSpace">
        Delete space
      </button>
    </template>

    <div class="mt-6 flex flex-wrap justify-between gap-2 border-t border-border pt-4">
      <button class="btn-outline" data-testid="show-space-qr" @click="showingQr = true">Show QR code</button>
      <button class="btn-ghost text-error hover:border-signal hover:text-error" :disabled="busy" data-testid="leave-space" @click="leave">
        Leave space
      </button>
    </div>
    </template>

    <!-- Roles: highest first; the order is the hierarchy -->
    <section v-else-if="shown.id === 'roles'" data-testid="roles-editor">
      <p class="mono mb-3 text-text-muted">
        Higher roles act on lower ones: removing, suspending, hiding messages, giving out roles. The owner is above every role;
        people without one are below.
      </p>
      <div
        v-for="(r, i) in space.roles"
        :key="r.id"
        class="mb-3 border border-border p-3"
        :class="mine(i) ? '' : 'opacity-60'"
        :data-testid="`role-card-${r.name}`"
      >
        <div v-if="drafts[r.id]" class="space-y-2">
          <div class="flex items-center gap-2">
            <span class="mono w-5 shrink-0 text-text-disabled">{{ String(i + 1).padStart(2, '0') }}</span>
            <input
              v-model="drafts[r.id]!.name"
              class="input min-w-0 flex-1 py-1"
              maxlength="32"
              :disabled="!mine(i)"
              :aria-label="`Name of ${r.name}`"
            />
            <template v-if="mine(i)">
              <button
                class="btn-ghost px-2"
                :disabled="busy || !mine(i - 1)"
                :aria-label="`Move ${r.name} up`"
                data-testid="role-up"
                @click="moveRole(i, -1)"
              >↑</button>
              <button
                class="btn-ghost px-2"
                :disabled="busy || i === space.roles.length - 1"
                :aria-label="`Move ${r.name} down`"
                data-testid="role-down"
                @click="moveRole(i, 1)"
              >↓</button>
              <button class="btn-ghost px-2" :disabled="busy || !dirty(r) || !drafts[r.id]!.name.trim()" data-testid="role-save" @click="saveRole(r)">Save</button>
              <button class="btn-ghost px-2 text-error hover:border-signal hover:text-error" :disabled="busy" :aria-label="`Delete ${r.name}`" data-testid="role-delete" @click="deleteRole(r)">✕</button>
            </template>
          </div>
          <p class="mono text-text-muted">
            {{ String(holders(r)).padStart(2, '0') }} {{ holders(r) === 1 ? 'person' : 'people' }}<template v-if="!mine(i)"> · at or above your role</template>
          </p>
          <div class="flex gap-1" role="radiogroup" :aria-label="`Colour of ${r.name}`">
            <button
              v-for="c in ROLE_COLORS"
              :key="c"
              type="button"
              role="radio"
              class="h-4 w-4 border"
              :class="drafts[r.id]!.color === c ? 'border-ink outline outline-1 outline-offset-1 outline-ink' : 'border-border'"
              :style="{ background: c }"
              :disabled="!mine(i)"
              :aria-checked="drafts[r.id]!.color === c"
              :aria-label="c"
              @click="drafts[r.id]!.color = c"
            />
          </div>
          <label v-for="p in PERMS" :key="p.perm" class="flex cursor-pointer items-center gap-2 text-sm" :title="p.hint">
            <input
              type="checkbox"
              :checked="drafts[r.id]!.perms.includes(p.perm)"
              :disabled="permDisabled(drafts[r.id]!.perms, p.perm, mine(i))"
              :data-testid="`role-perm-${p.perm}`"
              @change="togglePerm(drafts[r.id]!, p.perm)"
            />
            {{ p.label }}
          </label>
        </div>
      </div>
      <form class="space-y-2 border border-dashed border-border-strong p-3" data-testid="new-role" @submit.prevent="createRole">
        <div class="flex gap-2">
          <input v-model="fresh.name" class="input min-w-0 flex-1 py-1" maxlength="32" placeholder="New role, e.g. Crew" data-testid="new-role-name" />
          <button class="btn-primary" :disabled="busy || !fresh.name.trim()" data-testid="new-role-create">Create</button>
        </div>
        <p class="mono text-text-muted">New roles start at the bottom.</p>
        <div class="flex gap-1" role="radiogroup" aria-label="Colour">
          <button
            v-for="c in ROLE_COLORS"
            :key="c"
            type="button"
            role="radio"
            class="h-4 w-4 border"
            :class="fresh.color === c ? 'border-ink outline outline-1 outline-offset-1 outline-ink' : 'border-border'"
            :style="{ background: c }"
            :aria-checked="fresh.color === c"
            :aria-label="c"
            @click="fresh.color = c"
          />
        </div>
        <label v-for="p in PERMS" :key="p.perm" class="flex cursor-pointer items-center gap-2 text-sm" :title="p.hint">
          <input
            type="checkbox"
            :checked="fresh.perms.includes(p.perm)"
            :disabled="permDisabled(fresh.perms, p.perm)"
            :data-testid="`new-role-perm-${p.perm}`"
            @change="togglePerm(fresh, p.perm)"
          />
          {{ p.label }}
        </label>
      </form>
    </section>

    <!-- Users: every member, with their role -->
    <section v-else-if="shown.id === 'members'" data-testid="users-tab">
      <input
        v-if="space.members.length > 8"
        v-model="userQuery"
        type="search"
        class="input mb-3 w-full py-1"
        placeholder="Search user@host or name"
        aria-label="Search members"
        data-testid="users-search"
      />
      <p v-if="users.length === 0" class="mono text-text-muted">No one matches.</p>
      <div v-for="m in users" :key="m.profile.user_id" class="mb-2 flex items-center gap-2" :data-testid="`member-row-${m.profile.display_name}`">
        <span class="min-w-0 flex-1 text-sm leading-tight">
          <span class="block truncate" data-testid="member-user-id">{{ m.profile.user_id }}</span>
          <span class="block truncate text-xs text-text-muted">
            {{ m.profile.display_name }}<span v-if="m.profile.user_id === session.me" class="mono ml-1.5 text-text-disabled uppercase">you</span>
          </span>
        </span>
        <span v-if="m.role === 'owner'" class="mono w-40 text-text-muted uppercase" data-testid="member-owner">Owner</span>
        <select
          v-else
          class="input w-40 py-1"
          :value="roleOf(space, m)?.id ?? ''"
          :disabled="busy || !mine(rankOf(space, m))"
          :aria-label="`Role of ${m.profile.display_name}`"
          :data-testid="`role-${m.profile.display_name}`"
          @change="setMemberRole(m, ($event.target as HTMLSelectElement).value)"
        >
          <option value="">No role</option>
          <option v-for="(r, i) in space.roles" :key="r.id" :value="r.id" :disabled="!mine(i)">{{ r.name }}</option>
        </select>
      </div>
    </section>
  </Modal>
</template>
